import React, { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useSelector } from "react-redux"

import CheckIcon from "@mui/icons-material/Check"
import ClearIcon from "@mui/icons-material/Clear"
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline"
import { Grid } from "@mui/material"
import { Button, InputText, NotificationUtils, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { KonanAPI } from "../../services/KonanAPI"
import { queryClient } from "../../store/QueryClientStore"
import type { RootState } from "../../store/ReduxStore"
import type { Label } from "../../types/generated/api/Label"
import type { PaginatedLabelList } from "../../types/generated/api/PaginatedLabelList"
import { useIsPermitted } from "../../utils/PermissionsHelpers"
import { PROHIBITED_LABELS_KEYWORDS } from "../../utils/constants"
import { isInputValueValid } from "../../utils/genericHelpers"
import { KonanPagination } from "./KonanPagination"

const MemoizedCustomPagination = React.memo(KonanPagination)

interface ItemProps {
  label: Label
  isNew?: boolean
  onNewItemSubmit?: () => void
}

function TableItem(props: Readonly<ItemProps>): React.ReactElement {
  const { label, isNew = false, onNewItemSubmit } = props

  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)
  const currentProject = useSelector((state: RootState) => state.project.currentProject)

  // Permission check constants
  const canAddLabel = useIsPermitted("Add label", permissions.konan, flattenedKonanPermissions)
  const canDeleteLabel = useIsPermitted("Delete label", permissions.konan, flattenedKonanPermissions)

  const { t } = useTranslation("tables", {
    keyPrefix: "labels",
  })

  const [name, setName] = useState(isNew ? "" : label.name)

  const CreateLabelMutation = useMutation<
    AxiosResponse,
    AxiosError,
    {
      project_uuid: string
      name: string
    }
  >(KonanAPI.CreateLabel, {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.created"), { severity: "success" })

      await queryClient.invalidateQueries(["label-list", currentProject?.uuid])
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.creationFailed"), { severity: "error" })
    },
  })

  const DeleteLabelMutation = useMutation<
    AxiosResponse,
    AxiosError,
    {
      project_uuid: string
      label_uuid: string
    }
  >(KonanAPI.DeleteLabel, {
    onSuccess: async () => {
      NotificationUtils.toast(t("notifications.deleted"), { severity: "success" })

      await queryClient.invalidateQueries(["label-list", currentProject?.uuid])
    },
    onError: () => {
      NotificationUtils.toast(t("notifications.deletionFailed"), { severity: "error" })
    },
  })

  const handleCreateLabel = async (): Promise<void> => {
    if (isInputValueValid("label", name, PROHIBITED_LABELS_KEYWORDS)) {
      await CreateLabelMutation.mutateAsync({
        project_uuid: currentProject?.uuid as string,
        name: name?.trim(),
      })

      onNewItemSubmit?.()
    }
  }

  return (
    <Grid
      container
      key={label?.uuid}
      item
      xs={12}
      px={1.5}
      py={0.75}
      style={{ borderBottom: "1px solid var(--base-border-1)" }}
    >
      <Grid item xs={8}>
        {isNew ? (
          <InputText
            hideDescription
            placeholder={t("placeholder")}
            value={name}
            handleChange={(e) => setName(e.target.value as string)}
          />
        ) : (
          <Typography variant="p">{label?.name}</Typography>
        )}
      </Grid>

      <Grid container item xs={4} gap={1} alignContent={"center"}>
        {canAddLabel && isNew && (
          <Fragment>
            <Grid item>
              <Button
                disabled={(isNew && name === "") || CreateLabelMutation.isLoading}
                onClick={handleCreateLabel}
                isLoading={CreateLabelMutation.isLoading}
                size="small"
              >
                <CheckIcon fontSize="inherit" style={{ fill: "var(--green-background-1)", fontSize: "16px" }} />
              </Button>
            </Grid>

            <Grid item>
              <Button size="small" onClick={() => onNewItemSubmit?.()}>
                <ClearIcon fontSize="inherit" style={{ fill: "var(--red-background-1)", fontSize: "16px" }} />
              </Button>
            </Grid>
          </Fragment>
        )}

        {!isNew && canDeleteLabel && (
          <Button
            variant={"ghost"}
            size="small"
            onClick={async () =>
              await DeleteLabelMutation.mutateAsync({
                project_uuid: currentProject?.uuid as string,
                label_uuid: label?.uuid,
              })
            }
            isLoading={DeleteLabelMutation.isLoading}
          >
            <RemoveCircleOutlineIcon
              fontSize="inherit"
              style={{ fill: "var(--negative-icon-default)", fontSize: "16px" }}
            />
          </Button>
        )}
      </Grid>
    </Grid>
  )
}

interface TableProps {
  labels?: PaginatedLabelList
  page: number
  setPage: (page: number) => void
}

export function LabelsTable(props: Readonly<TableProps>): React.ReactElement {
  const { labels, page, setPage } = props
  const { t } = useTranslation("tables")

  const permissions = useSelector((state: RootState) => state.auth.permissions)

  const [hasNewLabel, setHasNewLabel] = useState<boolean>(false)

  const flattenedKonanPermissions = useSelector((state: RootState) => state.permissions.flattenedKonanPermissions)

  const canAddLabel = useIsPermitted("Add label", permissions.konan, flattenedKonanPermissions)

  return (
    <Grid container xs={12}>
      {/* Table Header */}
      <Grid container item xs={12} px={1.5} py={0.75} style={{ borderBottom: "1px solid var(--base-border-1)" }}>
        <Grid item xs={8}>
          <Typography variant={"a"}>{t("labels.headers.labels")}</Typography>
        </Grid>
        <Grid item xs={4}>
          <Typography variant={"a"}>{t("labels.headers.actions")}</Typography>
        </Grid>
      </Grid>

      {canAddLabel && (
        <Grid item xs={12} px={1.5} py={0.75} style={{ borderBottom: "1px solid var(--base-border-1)" }}>
          <Button disabled={hasNewLabel} size="small" onClick={() => setHasNewLabel(true)}>
            {t("labels.buttons.addLabel")}
          </Button>
        </Grid>
      )}

      {hasNewLabel && (
        <TableItem
          onNewItemSubmit={() => {
            setHasNewLabel(false)
          }}
          label={{ uuid: "", name: "", created_at: "", created_by: "" }}
          isNew
        />
      )}

      {labels?.results?.map((item) => (
        <TableItem key={item.uuid} label={item} />
      ))}

      <MemoizedCustomPagination
        count={labels?.count ?? 0}
        page={page}
        onPageChange={(index: number) => {
          setPage(index)
        }}
        rowsPerPage={10}
      />
    </Grid>
  )
}
